<div class="lg:grid lg:grid-cols-[minmax(0,1fr)_400px] lg:items-start lg:gap-12 xl:grid-cols-[minmax(0,1fr)_440px] xl:gap-20">
  <!-- Left column: form -->
  <div class="max-w-2xl">
    <h1 class="text-4xl font-extrabold tracking-tight text-gray-900">Let's get your venue set up.</h1>
    <p class="mt-3 text-lg text-gray-600">
      Tell us where to reach you and we'll take it from there. No card, no contract.
    </p>

    <form>
      <div class="mt-8 max-w-lg">
        <label for="email" class="field-label">Email Address</label>
        <input
          type="email"
          id="email"
          placeholder="you@example.com"
          class="input-field"
          [(ngModel)]="email"
          name="email"
          (ngModelChange)="onFieldChange()"
        />
      </div>

      <div class="mt-4 flex max-w-xl items-start gap-3 rounded-lg bg-gray-100 p-4">
        <span class="text-sm font-bold italic text-red-700" aria-hidden="true">i</span>
        <p class="text-sm text-gray-600">
          No password needed — when your venue is ready we'll email you an invite
          to set up your account in the BlazeBite portal.
        </p>
      </div>

      <hr class="my-10 border-gray-200" />

      <h2 class="text-2xl font-extrabold tracking-tight text-gray-900">Tell us about your business</h2>
      <p class="mt-1 text-gray-600">Together let's tailor Blazebite for your business.</p>

      <div class="mt-6 grid gap-6 sm:grid-cols-[3fr_2fr]">
        <div>
          <label for="business-name" class="field-label">Business Name</label>
          <input
            type="text"
            id="business-name"
            placeholder="Your business name"
            class="input-field"
            [(ngModel)]="businessName"
            name="businessName"
            (ngModelChange)="onFieldChange()"
          />
          <p class="mt-2 text-sm text-gray-500">
            Your business name is how we'll identify you on emails, receipts, and messages to customers.
          </p>
        </div>
        <div>
          <label for="locale" class="field-label">Locale</label>
          <select
            id="locale"
            class="input-field"
            [(ngModel)]="locale"
            name="locale"
            (ngModelChange)="onFieldChange()"
          >
            <option value="en-US">English (United States)</option>
            <option value="en-CA">English (Canada)</option>
            <option value="fr-CA">French (Canada)</option>
          </select>
        </div>
      </div>

      <hr class="my-10 border-gray-200" />

      <h2 class="text-2xl font-extrabold tracking-tight text-gray-900">Business Contact Info</h2>
      <p class="mt-1 text-gray-600">Where can customers reach your business?</p>

      <div class="mt-6 grid gap-6 sm:grid-cols-2">
        <div>
          <label for="contactPerson" class="field-label">Contact Person</label>
          <input
            type="text"
            id="contactPerson"
            placeholder="Full name"
            class="input-field"
            [(ngModel)]="contactPerson"
            name="contactPerson"
            (ngModelChange)="onFieldChange()"
          />
        </div>
        <div>
          <label for="contactPhoneNumber" class="field-label">Phone Number</label>
          <input
            type="tel"
            id="contactPhoneNumber"
            placeholder="(555) 123-4567"
            class="input-field"
            [(ngModel)]="contactPhoneNumber"
            name="contactPhoneNumber"
            (ngModelChange)="onFieldChange()"
          />
        </div>
      </div>

      <div class="mt-6">
        <label for="contactEmail" class="field-label">Contact Email</label>
        <input
          type="email"
          id="contactEmail"
          class="input-field"
          [placeholder]="sameAsLoginEmail && email ? email : 'contact@yourbusiness.com'"
          [(ngModel)]="contactEmail"
          name="contactEmail"
          (ngModelChange)="onFieldChange()"
          [disabled]="sameAsLoginEmail"
        />
        <label for="sameAsLogin" class="mt-3 flex w-fit cursor-pointer items-center gap-2.5 text-base text-gray-700">
          <input
            type="checkbox"
            id="sameAsLogin"
            class="h-5 w-5 accent-red-700"
            [(ngModel)]="sameAsLoginEmail"
            name="sameAsLoginEmail"
            (ngModelChange)="onSameAsLoginEmailChange()"
          />
          Same as login email
        </label>
      </div>

      <div class="mt-6">
        <label for="streetAddress" class="field-label">Street Address</label>
        <input
          type="text"
          id="streetAddress"
          placeholder="123 Main Street"
          class="input-field"
          [(ngModel)]="streetAddress"
          name="streetAddress"
          (ngModelChange)="onFieldChange()"
        />
      </div>

      <div class="mt-6 grid gap-6 sm:grid-cols-2">
        <div>
          <label for="city" class="field-label">City</label>
          <input
            type="text"
            id="city"
            placeholder="City"
            class="input-field"
            [(ngModel)]="city"
            name="city"
            (ngModelChange)="onFieldChange()"
          />
        </div>
        <div>
          <label for="country" class="field-label">Country</label>
          <select
            id="country"
            class="input-field"
            [(ngModel)]="country"
            name="country"
            (ngModelChange)="onCountryChange()"
          >
            <option value="US">United States</option>
            <option value="CA">Canada</option>
          </select>
        </div>
      </div>

      <div class="mt-6 grid grid-cols-2 gap-6">
        <div>
          <label for="stateProvince" class="field-label">State/Province</label>
          <select
            id="stateProvince"
            class="input-field"
            [(ngModel)]="stateProvince"
            name="stateProvince"
            (ngModelChange)="onFieldChange()"
          >
            <option value="">Select...</option>
            <option *ngFor="let state of getStatesForCountry()" [value]="state.code">
              {{ state.name }}
            </option>
          </select>
        </div>
        <div>
          <label for="postalCode" class="field-label">Postal Code</label>
          <input
            type="text"
            id="postalCode"
            [placeholder]="country === 'CA' ? 'A1A 1A1' : '12345'"
            class="input-field"
            [(ngModel)]="postalCode"
            name="postalCode"
            (ngModelChange)="onFieldChange()"
          />
        </div>
      </div>

      <hr class="my-10 border-gray-200" />

      <label for="agree" class="flex w-fit cursor-pointer items-start gap-3 text-base text-gray-900">
        <input
          type="checkbox"
          id="agree"
          class="mt-0.5 h-5 w-5 shrink-0 accent-red-700"
          [(ngModel)]="agreeTerms"
          name="agreeTerms"
          (ngModelChange)="onFieldChange()"
        />
        <span>
          I agree to Blazebite's
          <a [href]="termsUrl"
             class="font-semibold text-red-700 underline underline-offset-2 hover:text-red-800"
             target="_blank"
             rel="noopener noreferrer">Terms</a>,
          <a [href]="privacyUrl"
             class="font-semibold text-red-700 underline underline-offset-2 hover:text-red-800"
             target="_blank"
             rel="noopener noreferrer">Privacy Policy</a>, and
          <a [href]="eSignUrl"
             class="font-semibold text-red-700 underline underline-offset-2 hover:text-red-800"
             target="_blank"
             rel="noopener noreferrer">E-Sign Consent</a>.
        </span>
      </label>

      <div class="mt-8 flex flex-col items-center gap-4 sm:flex-row">
        <button
          type="button"
          class="btn-brand w-full sm:w-auto"
          (click)="submitForm()"
          [disabled]="!isFormComplete"
        >
          Continue
        </button>
        <p class="text-sm text-gray-500">
          {{ isFormComplete ? 'Takes about two more minutes.' : 'Fill in every field above to continue.' }}
        </p>
      </div>

      <div class="mt-8 text-center text-sm text-gray-500 lg:hidden">
        Already invited?
        <a
          [href]="portalUrl"
          class="font-semibold text-red-700 underline underline-offset-2 hover:text-red-800">
          Sign in to the portal
        </a>
      </div>

      <p class="mt-8 text-xs text-gray-400">
        This site is protected by reCAPTCHA Enterprise and the Google
        <a
          href="https://policies.google.com/privacy"
          class="underline underline-offset-2 hover:text-gray-600"
          target="_blank"
          rel="noopener noreferrer"
        >Privacy Policy</a>
        and
        <a
          href="https://policies.google.com/terms"
          class="underline underline-offset-2 hover:text-gray-600"
          target="_blank"
          rel="noopener noreferrer"
        >Terms of Service</a>
        apply.
      </p>
    </form>
  </div>

  <!-- Right column: sidebar (desktop only) -->
  <aside class="hidden overflow-hidden rounded-2xl bg-gray-100 lg:sticky lg:top-8 lg:flex lg:flex-col">
    <img
      src="assets/stock/concession3.png"
      alt="Staff serving customers at a concession window"
      class="h-52 w-full object-cover xl:h-64"
    />
    <div class="p-8">
      <h2 class="text-xs font-semibold uppercase tracking-widest text-gray-500">What happens next</h2>
      <ol class="mt-5 space-y-5">
        <li class="flex gap-3">
          <span class="font-bold text-red-700">1</span>
          <div>
            <p class="font-bold text-gray-900">We review your details</p>
            <p class="mt-0.5 text-gray-600">Usually within one business day.</p>
          </div>
        </li>
        <li class="flex gap-3">
          <span class="font-bold text-red-700">2</span>
          <div>
            <p class="font-bold text-gray-900">You get an invite email</p>
            <p class="mt-0.5 text-gray-600">That's where you set your password and open the portal.</p>
          </div>
        </li>
        <li class="flex gap-3">
          <span class="font-bold text-red-700">3</span>
          <div>
            <p class="font-bold text-gray-900">Build your menu and go live</p>
            <p class="mt-0.5 text-gray-600">Most venues are taking orders at their next event.</p>
          </div>
        </li>
      </ol>

      <hr class="my-8 border-gray-200" />

      <blockquote>
        <p class="text-lg font-bold leading-snug text-gray-900">
          "We set it up between two games. The line moved twice as fast that night."
        </p>
        <footer class="mt-3 text-sm text-gray-600">Concessions lead, high-school booster club</footer>
      </blockquote>
    </div>
    <div class="mt-auto border-t border-gray-200 px-8 py-5 text-sm text-gray-500">
      Already invited?
      <a
        [href]="portalUrl"
        class="font-semibold text-red-700 underline underline-offset-2 hover:text-red-800">
        Sign in to the portal
      </a>
    </div>
  </aside>
</div>
